/**
 * Фаза ОРИЕНТАЦИЯ (спека 05 v2): показать «куда двигаться» ДО начала движения
 * (GPS стоя слеп). Переходы: route → orient → follow. L0 — фраза от щита,
 * L1 — живая стрелка после привязки одним тапом (OrientationService).
 */
import { haversineM } from '@app/geo'
import type { I18nKey } from '@app/i18n'
import type { Anchor, RoutePlan, Venue } from '@app/rvf'
import { startFollow } from './actions'
import { dropWarmup, warmupAr } from './ar/permissions'
import { firstLeg, OrientationService, type Turn, turnFromBoard } from './nav/orientation'
import {
  anchorId,
  appState,
  guidancePref,
  orientBinding,
  orientHeading,
  orientSensors,
  orientStale,
  routePlan,
  venue,
} from './state'

let service: OrientationService | null = null
let gpsWatchId: number | null = null

/** Якорь пригоден для фразы/привязки: маршрут реально стартует от него. */
const ANCHOR_NEAR_M = 25

export interface OrientContext {
  readonly phraseKey: I18nKey | null
  readonly firstLegM: number | null
  readonly targetBearingDeg: number | null
  readonly anchorHeadingDeg: number | null
}

/** Якорь текущего старта — только если маршрут начинается рядом с ним. */
export function orientAnchor(v: Venue, plan: RoutePlan): Anchor | null {
  const anchor = v.anchors.find((a) => a.id === anchorId.value)
  if (!anchor || typeof anchor.heading !== 'number') return null
  return haversineM(anchor.point, plan.start.point) <= ANCHOR_NEAR_M ? anchor : null
}

const BOARD_PHRASES: Readonly<Record<Turn, I18nKey>> = {
  straight: 'orient.board_straight',
  right: 'orient.board_right',
  left: 'orient.board_left',
  back: 'orient.board_back',
}

/** B3: транспортная фраза — от геометрии дороги, НИКОГДА от ориентации кабины. */
const GATE_PHRASES: Readonly<Record<Turn, I18nKey>> = {
  straight: 'orient.gate_straight',
  right: 'orient.gate_right',
  left: 'orient.gate_left',
  back: 'orient.gate_back',
}

/** Контекст экрана: фраза L0, дистанция первого плеча, азимут цели для стрелки. */
export function orientContext(v: Venue, plan: RoutePlan): OrientContext {
  const leg = firstLeg(plan.polyline)
  const anchor = orientAnchor(v, plan)
  if (!leg) {
    return { phraseKey: null, firstLegM: null, targetBearingDeg: null, anchorHeadingDeg: null }
  }
  if (!anchor) {
    // Старт не от щита (гео/вход/обратный путь) — фраза от предмета невозможна.
    return {
      phraseKey: null,
      firstLegM: leg.lengthM,
      targetBearingDeg: leg.bearingDeg,
      anchorHeadingDeg: null,
    }
  }
  const heading = anchor.heading as number
  const turn = turnFromBoard(leg.bearingDeg, heading)
  const driver = anchor.placement?.['audience'] === 'driver'
  return {
    phraseKey: (driver ? GATE_PHRASES : BOARD_PHRASES)[turn],
    firstLegM: leg.lengthM,
    targetBearingDeg: leg.bearingDeg,
    anchorHeadingDeg: heading,
  }
}

/** «Поехали» из превью маршрута: показать фазу ориентации. */
export function enterOrient(): void {
  const plan = routePlan.value
  if (!plan || appState.value !== 'route') return
  appState.value = 'orient'
  watchGpsForAutoGo()
}

/** Авто-переход в follow, когда GPS поймал курс (пользователь пошёл/поехал). */
function watchGpsForAutoGo(): void {
  if (!navigator.geolocation || gpsWatchId !== null) return
  gpsWatchId = navigator.geolocation.watchPosition(
    (pos) => {
      const moving = (pos.coords.speed ?? 0) > 0.7 && pos.coords.heading !== null
      if (moving && appState.value === 'orient') confirmGo()
    },
    () => undefined,
    { enableHighAccuracy: true, maximumAge: 3000, timeout: 20_000 },
  )
}

/** Тап «привязать стрелку»: iOS-разрешение живёт только в обработчике тапа. */
export async function bindArrow(): Promise<void> {
  const v = venue.value
  const plan = routePlan.value
  if (!v || !plan) return
  const anchor = orientAnchor(v, plan)
  if (!anchor) return
  if (!service) service = new OrientationService()
  const access = await service.start(() => {
    if (!service?.bound) return
    orientHeading.value = service.worldHeading()
    orientStale.value = service.isStale()
  })
  orientSensors.value = access
  if (access !== 'granted') return
  // Первое показание датчика может прийти с задержкой — короткое ожидание.
  const heading = anchor.heading as number
  for (let attempt = 0; attempt < 10; attempt++) {
    if (service.bind(heading)) {
      orientHeading.value = service.worldHeading()
      // Привязка переносится в AR-ведение (бейдж «Якорь QR» вместо компаса).
      orientBinding.value = service.binding
      return
    }
    await new Promise((resolve) => setTimeout(resolve, 150))
  }
  // Показаний так и нет (датчик мёртв) — честно откатываемся на L0.
  orientSensors.value = 'unsupported'
}

/** «Иду ✓» (или авто по GPS-курсу): в ведение. */
export function confirmGo(): void {
  if (guidancePref.value === 'ar') {
    // Сохранённый выбор AR: разрешения просим ПРЯМО В ЭТОМ тапе (iOS) —
    // оверлей дождётся результата через takeWarmup(). При авто-переходе по
    // GPS-курсу жеста нет — тогда честный фолбэк внутри AR (ar.sensors_denied).
    void warmupAr(true)
  } else {
    // Разогретая камера нужна только AR-ведению; выбрана карта — гасим честно.
    dropWarmup()
  }
  cleanupOrient()
  void startFollow()
}

/** Назад из ориентации — к превью маршрута. */
export function exitOrient(): void {
  dropWarmup()
  cleanupOrient()
  appState.value = 'route'
}

function cleanupOrient(): void {
  if (gpsWatchId !== null) {
    navigator.geolocation.clearWatch(gpsWatchId)
    gpsWatchId = null
  }
  service?.stop()
  service = null
  orientHeading.value = null
  orientSensors.value = 'idle'
  orientStale.value = false
}
